import React, { useCallback, useState, useEffect } from 'react';
import { Spin, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { CMS_SEARCH_PROPERTIES_BY_NAME } from './query';
import { debounce } from 'lodash';
import { MinusCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { v4 as uuidV4 } from 'uuid';

export default function SelectPropertyByProperty(props) {
  const { selected, onChange } = props;
  const { t } = useTranslation();
  const [selectedProperties, setSelectedProperties] = useState(() => {
    if (selected.length) {
      return selected.map(item => ({
        id: uuidV4(),
        value: item,
      }));
    } else {
      return [{ id: uuidV4(), value: '' }];
    }
  });

  const handleOnselect = (newValue, index) => {
    setSelectedProperties(newestProperties => {
      const editingProperties = [...newestProperties];
      editingProperties[index] = {
        id: editingProperties[index].id,
        value: newValue,
      };
      onChange(editingProperties.map(pp => pp.value).filter(pp => pp.length));
      return editingProperties;
    });
  };

  const handleAdd = useCallback(() => {
    setSelectedProperties(current => current.concat([{ id: uuidV4(), value: '' }]));
  }, []);

  const handleRemove = useCallback(index => {
    setSelectedProperties(current => {
      current.splice(index, 1);
      onChange(current.map(pp => pp.value).filter(pp => pp.length));
      return [...current];
    });
  }, []);

  return (
    <div className="select-property-by-property">
      <h5 className="select-property-by-property__title">
        {t('cms.special_offer.map.modal.property_selector.title')}:
      </h5>
      <div className="select-property-by-property__content">
        {selectedProperties.map((item, index) => (
          <div className="select-property-by-property__row" key={item.id}>
            <PropertySearchSelector
              selectedProperty={item.value}
              showPropertyCity
              onSelect={newValue => handleOnselect(newValue, index)}
            />
            <If condition={selectedProperties.length > 1}>
              <MinusCircleOutlined
                className="select-property-by-property__delete-icon"
                onClick={() => {
                  handleRemove(index);
                }}
              />
            </If>
          </div>
        ))}
        <div className="select-property-by-property__link" onClick={handleAdd}>
          {t('cms.table.header.button.add_new_property')}
        </div>
      </div>
    </div>
  );
}

export function PropertySearchSelector({
  selectedProperty,
  onSelect,
  propertyStatusFilter = 'PUBLISHED',
  className,
  showPropertyCity = false,
}) {
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [queryName, setQueryName] = useState('');
  const [properties, setProperties] = useState([]);
  const { t } = useTranslation();

  const { data: propertiesData, loading: getPropertiesLoading } = useQuery(
    CMS_SEARCH_PROPERTIES_BY_NAME,
    {
      variables: {
        query: queryName,
        pageNumber: currentPageNumber,
        pageSize: 20,
        publishedStatus: propertyStatusFilter,
      },
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    const responseData = propertiesData?.search?.edges?.map(edge => edge.node) || [];
    if (queryName) {
      return setProperties(responseData);
    } else {
      setProperties(current => current.concat(responseData));
    }
  }, [propertiesData]);

  const handlePropertySelect = useCallback(
    v => {
      const option = properties.find(p => p.id === v);

      onSelect(v, option);
    },
    [properties],
  );

  const handleSearch = useCallback(v => {
    setQueryName(v);
    setCurrentPageNumber(1);
  }, []);
  const requestSearch = useCallback(debounce(handleSearch, 2000), []);
  return (
    <div className="select-property-by-property__selector-container">
      <Select
        showSearch
        className={classNames('select-property-by-property__selector', className)}
        getPopupContainer={() =>
          document.querySelector('.select-property-by-property__selector-container')
        }
        value={selectedProperty}
        defaultActiveFirstOption={false}
        showArrow={false}
        filterOption={false}
        loading={getPropertiesLoading}
        placeholder={t('cms.special_offer.map.modal.property_selector.placeholder')}
        notFoundContent={
          <If condition={getPropertiesLoading}>
            <Spin size="small" />
          </If>
        }
        onPopupScroll={e => {
          e.persist();
          const target = e.target;
          if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
            if (propertiesData?.search?.pageInfo.hasNextPage && !getPropertiesLoading) {
              setCurrentPageNumber(currentNumber => currentNumber + 1);
            }
          }
        }}
        onSearch={requestSearch}
        onSelect={handlePropertySelect}
      >
        <For each="d" of={properties}>
          <Select.Option value={d.id} key={d.id}>
            {d.name}
            {showPropertyCity && (
              <span className="select-property-by-property__selector__city">{d.city.name}</span>
            )}
          </Select.Option>
        </For>
      </Select>
    </div>
  );
}
